import * as React from 'react'
import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router'
import { AppShell } from '@/components/app-shell'
import { NotesProvider } from '@/state/notes-context'
import { MealsProvider } from '@/state/use-meal-plan'
import type { FamilyUser } from '@shared/types'

const PUBLIC_ROUTES = ['/sign-in', '/sign-up', '/pending', '/reset-password']
const CACHED_IDENTITY = 'family-notes:last-approved-user'

function cachedIdentity(): { user: FamilyUser; users: FamilyUser[] } | null {
  try {
    const value = localStorage.getItem(CACHED_IDENTITY)
    return value ? JSON.parse(value) : null
  } catch { return null }
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: () => <div className="grid min-h-screen place-items-center"><div><h1 className="text-3xl font-bold">Page not found</h1><a className="mt-3 inline-block text-[var(--foreground)] underline underline-offset-4" href="/">Back to notes</a></div></div>,
})

function RootComponent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const isPublic = PUBLIC_ROUTES.some((route) => pathname.startsWith(route))
  const [identity, setIdentity] = React.useState<{ user: FamilyUser; users: FamilyUser[] } | null>(null)
  const updateCurrentUser = React.useCallback((patch: Partial<Pick<FamilyUser, 'name' | 'image'>>) => {
    setIdentity((current) => {
      if (!current) return current
      const next = {
        user: { ...current.user, ...patch },
        users: current.users.map((user) => user.id === current.user.id ? { ...user, ...patch } : user),
      }
      try { localStorage.setItem(CACHED_IDENTITY, JSON.stringify(next)) } catch { /* Server profile remains saved. */ }
      return next
    })
  }, [])
  React.useEffect(() => {
    if (isPublic) return
    let cancelled = false
    async function load() {
      try {
        const response = await fetch('/api/session', { credentials: 'same-origin' })
        if (response.status === 401) { localStorage.removeItem(CACHED_IDENTITY); location.replace('/sign-in'); return }
        if (!response.ok) throw new Error('Session unavailable')
        const payload = await response.json()
        const user = payload.session?.user as FamilyUser & { emailVerified?: boolean }
        if (!user?.emailVerified) { location.replace('/pending'); return }
        const familyResponse = await fetch('/api/bootstrap', { credentials: 'same-origin' })
        if (familyResponse.status === 403) { localStorage.removeItem(CACHED_IDENTITY); location.replace('/pending'); return }
        if (!familyResponse.ok) throw new Error('Family unavailable')
        const family = await familyResponse.json()
        const familyUsers = family.users as FamilyUser[]
        const next = { user: familyUsers.find((member) => member.id === user.id) ?? user, users: familyUsers }
        localStorage.setItem(CACHED_IDENTITY, JSON.stringify(next))
        if (!cancelled) setIdentity(next)
      } catch {
        const cached = cachedIdentity()
        if (cached) { if (!cancelled) setIdentity(cached); return }
        if (!cancelled) location.replace('/sign-in')
      }
    }
    void load()
    return () => { cancelled = true }
  }, [isPublic])
  if (isPublic) return <Outlet />
  if (!identity) return <div className="grid min-h-screen place-items-center text-[var(--muted-foreground)]">Opening your notes…</div>
  return <NotesProvider key={identity.user.id} currentUser={identity.user} users={identity.users} onCurrentUserChange={updateCurrentUser}><MealsProvider><AppShell><Outlet /></AppShell></MealsProvider></NotesProvider>
}
